import * as React from "react";
import {
  Card,
  Subtitle1,
  Text,
  Input,
  Textarea,
  Button,
  Badge,
  Spinner,
  MessageBar,
  MessageBarBody,
  Accordion,
  AccordionItem,
  AccordionHeader,
  AccordionPanel,
  Dialog,
  DialogSurface,
  DialogBody,
  DialogTitle,
  DialogContent,
  DialogActions,
  Field,
  makeStyles,
  mergeClasses,
  shorthands,
  tokens,
} from "@fluentui/react-components";
import {
  Edit20Regular,
  Save20Regular,
  Dismiss20Regular,
  Search20Regular,
  Add20Regular,
  Delete20Regular,
  EyeOff20Regular,
  Eye20Regular,
  Rename20Regular,
} from "@fluentui/react-icons";
import type { BrancheLeaf } from "../../data/brancheClassification";
import { baueBaum, katalogFiltern, katalogUmfang } from "./branchenKatalogLogik";
import {
  blattAendern,
  blattAnlegen,
  blattLoeschen,
  brancheUmbenennen,
  gruppeUmbenennen,
  katalogLaden,
  verwendungLaden,
  type KatalogVerwendung,
} from "../../services/brancheKatalogService";

/**
 * Einstellungen → Branchen-Katalog: Pflege der dreistufigen
 * Branchenklassifizierung (Branche → Branchengruppe → Spezialisierung).
 *
 * WARUM „neue Gruppe" immer nach einer Spezialisierung fragt: Ebene 1 und 2
 * haben keine eigenen Tabellen — sie stehen denormalisiert an jedem Ebene-3-
 * Blatt, und `ebene3` ist NOT NULL und Teil des Unique-Index. Eine Gruppe
 * existiert also nur, solange sie mindestens ein Blatt hat. Statt eine leere
 * Gruppe zu erfinden (die beim nächsten Laden verschwände), verlangt die Maske
 * das erste Blatt gleich mit; dasselbe gilt für eine neue Branche.
 *
 * WARUM Umbenennen auf Ebene 1/2 eine eigene Aktion ist: der Name gehört ALLEN
 * Blättern der Gruppe bzw. Branche gemeinsam. Die Maske nennt deshalb vor dem
 * Speichern, wie viele Einträge die Umbenennung trifft.
 *
 * WARUM „löschen" nicht immer löscht: die Zuordnung an der Firma ist eine
 * Soft-Referenz auf die id (`Contact.brancheClassId`,
 * `ContactBrancheAssignment.brancheClassId`) ohne Fremdschlüssel. Ein hart
 * gelöschter Eintrag hinterließe dort eine tote id. Trägt also auch nur eine
 * Firma den Eintrag, wird er nur DEAKTIVIERT: raus aus der Auswahl in Anlage
 * und Akte, an den betroffenen Firmen aber weiterhin sichtbar.
 */

type Leaf = BrancheLeaf;

/** Welcher Dialog gerade offen ist (und womit er arbeitet). */
type DialogState =
  | { art: "neu-branche" }
  | { art: "neu-gruppe"; ebene1: string }
  | { art: "neu-spez"; ebene1: string; ebene2: string }
  | { art: "rename-branche"; ebene1: string; blaetter: number; gruppen: number }
  | { art: "rename-gruppe"; ebene1: string; ebene2: string; blaetter: number }
  | { art: "loeschen"; leaf: Leaf };

const useStyles = makeStyles({
  karte: { display: "flex", flexDirection: "column", rowGap: "16px", ...shorthands.padding("24px") },
  kopfzeile: {
    display: "flex",
    flexWrap: "wrap",
    alignItems: "flex-end",
    justifyContent: "space-between",
    columnGap: "12px",
    rowGap: "12px",
  },
  suchzeile: { display: "flex", flexWrap: "wrap", alignItems: "center", columnGap: "8px", rowGap: "8px" },
  suche: { maxWidth: "420px", flexGrow: 1 },
  aktionen: { display: "flex", flexWrap: "wrap", alignItems: "center", columnGap: "6px", rowGap: "6px" },
  kopfTitel: { display: "flex", alignItems: "center", columnGap: "8px", flexGrow: 1, minWidth: 0 },
  gruppen: { display: "flex", flexDirection: "column", rowGap: "14px", paddingBottom: "8px" },
  gruppenKopf: {
    display: "flex",
    flexWrap: "wrap",
    alignItems: "center",
    columnGap: "8px",
    rowGap: "4px",
    marginBottom: "6px",
  },
  gruppenName: { fontWeight: 600 },
  blaetter: { display: "flex", flexDirection: "column", rowGap: "8px" },
  blatt: {
    ...shorthands.border("1px", "solid", tokens.colorNeutralStroke2),
    ...shorthands.borderRadius("8px"),
    ...shorthands.padding("10px", "12px"),
    display: "flex",
    flexDirection: "column",
    rowGap: "6px",
  },
  // Deaktiviert heißt: bleibt lesbar, ist aber sichtbar aus dem Verkehr gezogen.
  blattInaktiv: { backgroundColor: tokens.colorNeutralBackground3, opacity: 0.75 },
  blattKopf: {
    display: "flex",
    flexWrap: "wrap",
    alignItems: "center",
    justifyContent: "space-between",
    columnGap: "8px",
    rowGap: "4px",
  },
  leer: { color: tokens.colorNeutralForeground4 },
  formular: { display: "flex", flexDirection: "column", rowGap: "12px" },
  fest: { color: tokens.colorNeutralForeground3 },
});

const anzahl = (n: number, eins: string, viele: string) => `${n} ${n === 1 ? eins : viele}`;

export function BranchenKatalogSection() {
  const s = useStyles();
  const [rows, setRows] = React.useState<Leaf[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [search, setSearch] = React.useState("");
  const [editId, setEditId] = React.useState<number | null>(null);
  const [editName, setEditName] = React.useState("");
  const [editText, setEditText] = React.useState("");
  const [busy, setBusy] = React.useState(false);
  const [msg, setMsg] = React.useState<{ text: string; kind: "success" | "error" } | null>(null);
  const [dialog, setDialog] = React.useState<DialogState | null>(null);

  const load = React.useCallback(async () => {
    setLoading(true);
    try {
      setRows(await katalogLaden());
    } catch (e) {
      setMsg({ text: (e as Error).message, kind: "error" });
    } finally {
      setLoading(false);
    }
  }, []);

  React.useEffect(() => {
    load();
  }, [load]);

  /**
   * Eine Änderung ausführen, melden und den Katalog neu laden.
   *
   * Rückgabe: `null` bei Erfolg, sonst der Fehlertext. Den braucht der Aufrufer,
   * weil ein Dialog bei einem Fehler offen bleibt und mit seinem Scrim über
   * dieser Karte liegt — die MessageBar hier unten sähe dann niemand.
   */
  const ausfuehren = async (aktion: () => Promise<string>): Promise<string | null> => {
    setBusy(true);
    setMsg(null);
    try {
      const text = await aktion();
      await load();
      setMsg({ text, kind: "success" });
      setDialog(null);
      return null;
    } catch (e) {
      const text = (e as Error).message;
      setMsg({ text, kind: "error" });
      return text;
    } finally {
      setBusy(false);
    }
  };

  const startEdit = (l: Leaf) => {
    setEditId(l.id);
    setEditName(l.ebene3);
    setEditText(l.erlaeuterung ?? "");
  };
  const cancel = () => {
    setEditId(null);
    setEditName("");
    setEditText("");
  };

  const speichereBlatt = (l: Leaf) =>
    ausfuehren(async () => {
      await blattAendern(l.id, { ebene3: editName.trim(), erlaeuterung: editText.trim() });
      cancel();
      return "Spezialisierung gespeichert.";
    });

  const aktivSchalten = (l: Leaf) =>
    ausfuehren(async () => {
      await blattAendern(l.id, { active: l.active === false });
      return l.active === false
        ? `„${l.ebene3}" ist wieder aktiv.`
        : `„${l.ebene3}" ist deaktiviert — neu wählbar ist die Spezialisierung nicht mehr.`;
    });

  const q = search.trim().toLowerCase();
  const filtered = React.useMemo(() => katalogFiltern(rows, q), [rows, q]);

  const tree = React.useMemo(() => baueBaum(filtered), [filtered]);
  const gesamt = React.useMemo(() => baueBaum(rows), [rows]);
  const inaktiv = rows.filter((r) => r.active === false).length;
  const gruppenGesamt = gesamt.reduce((n, b) => n + b.groups.length, 0);

  // Zahlen für die Umbenennen-Warnung IMMER aus dem ungefilterten Katalog —
  // siehe `katalogUmfang`.
  const umfang = React.useMemo(() => katalogUmfang(rows), [rows]);

  // Bei aktiver Suche klappen die Treffer-Branchen auf, beim Leeren wieder zu.
  // Der Ref hält den Zustand fest, wenn sich nur der Katalog geändert hat: sonst
  // klappte der Baum nach JEDEM Speichern zu — mitten in der Pflege.
  const [offen, setOffen] = React.useState<string[]>([]);
  const letzteSuche = React.useRef("");
  React.useEffect(() => {
    if (q === letzteSuche.current) return;
    letzteSuche.current = q;
    setOffen(q ? tree.map((n) => n.e1) : []);
  }, [q, tree]);

  return (
    <Card className={s.karte}>
      <div className={s.kopfzeile}>
        <div>
          <Subtitle1>Branchen-Katalog</Subtitle1>
          <div>
            <Text size={200}>
              {anzahl(gesamt.length, "Branche", "Branchen")} ·{" "}
              {anzahl(gruppenGesamt, "Gruppe", "Gruppen")} ·{" "}
              {anzahl(rows.length, "Spezialisierung", "Spezialisierungen")}
              {inaktiv > 0 ? ` (davon ${inaktiv} deaktiviert)` : ""}. Die Erläuterung erscheint
              als (i)-Tooltip in der Branche-Auswahl.
            </Text>
          </div>
        </div>
        <Button
          appearance="primary"
          icon={<Add20Regular />}
          disabled={busy || loading}
          onClick={() => setDialog({ art: "neu-branche" })}
        >
          Neue Branche
        </Button>
      </div>

      <div className={s.suchzeile}>
        <Input
          className={s.suche}
          placeholder="Branche / Gruppe / Spezialisierung suchen …"
          value={search}
          onChange={(_e, d) => setSearch(d.value)}
          contentBefore={<Search20Regular />}
        />
      </div>

      {msg ? (
        <MessageBar intent={msg.kind}>
          <MessageBarBody>{msg.text}</MessageBarBody>
        </MessageBar>
      ) : null}

      {loading ? (
        <Spinner label="Katalog wird geladen …" />
      ) : tree.length === 0 ? (
        <Text>Keine Treffer.</Text>
      ) : (
        <Accordion
          collapsible
          multiple
          openItems={offen}
          onToggle={(_e, d) => setOffen(d.openItems as string[])}
        >
          {tree.map((n) => {
            const blaetter = n.groups.reduce((sum, g) => sum + g.leaves.length, 0);
            return (
              <AccordionItem value={n.e1} key={n.e1}>
                <AccordionHeader>
                  <span className={s.kopfTitel}>
                    <span style={{ fontWeight: 600 }}>{n.e1}</span>
                    <Badge appearance="tint" color="informative">
                      {q ? `${blaetter} / ${umfang.branche(n.e1).blaetter}` : blaetter}
                    </Badge>
                  </span>
                </AccordionHeader>
                <AccordionPanel>
                  <div className={s.aktionen} style={{ marginBottom: 10 }}>
                    <Button
                      size="small"
                      appearance="subtle"
                      icon={<Rename20Regular />}
                      disabled={busy}
                      onClick={() =>
                        setDialog({
                          art: "rename-branche",
                          ebene1: n.e1,
                          blaetter: umfang.branche(n.e1).blaetter,
                          gruppen: umfang.branche(n.e1).gruppen,
                        })
                      }
                    >
                      Branche umbenennen
                    </Button>
                    <Button
                      size="small"
                      appearance="subtle"
                      icon={<Add20Regular />}
                      disabled={busy}
                      onClick={() => setDialog({ art: "neu-gruppe", ebene1: n.e1 })}
                    >
                      Neue Gruppe
                    </Button>
                  </div>

                  <div className={s.gruppen}>
                    {n.groups.map((g) => (
                      <div key={g.e2}>
                        <div className={s.gruppenKopf}>
                          <span className={s.gruppenName}>{g.e2}</span>
                          <Badge appearance="outline" color="informative">
                            {q
                              ? `${g.leaves.length} / ${umfang.gruppe(n.e1, g.e2)}`
                              : g.leaves.length}
                          </Badge>
                          <Button
                            size="small"
                            appearance="subtle"
                            icon={<Rename20Regular />}
                            disabled={busy}
                            onClick={() =>
                              setDialog({
                                art: "rename-gruppe",
                                ebene1: n.e1,
                                ebene2: g.e2,
                                blaetter: umfang.gruppe(n.e1, g.e2),
                              })
                            }
                          >
                            Gruppe umbenennen
                          </Button>
                          <Button
                            size="small"
                            appearance="subtle"
                            icon={<Add20Regular />}
                            disabled={busy}
                            onClick={() =>
                              setDialog({ art: "neu-spez", ebene1: n.e1, ebene2: g.e2 })
                            }
                          >
                            Neue Spezialisierung
                          </Button>
                        </div>

                        <div className={s.blaetter}>
                          {g.leaves.map((l) => {
                            const aus = l.active === false;
                            return (
                              <div
                                key={l.id}
                                className={mergeClasses(s.blatt, aus && s.blattInaktiv)}
                              >
                                <div className={s.blattKopf}>
                                  <Text weight="semibold">{l.ebene3}</Text>
                                  {aus ? (
                                    <Badge appearance="tint" color="warning">
                                      deaktiviert
                                    </Badge>
                                  ) : null}
                                  {editId !== l.id ? (
                                    <span className={s.aktionen}>
                                      <Button
                                        size="small"
                                        appearance="subtle"
                                        icon={<Edit20Regular />}
                                        disabled={busy}
                                        onClick={() => startEdit(l)}
                                      >
                                        Bearbeiten
                                      </Button>
                                      <Button
                                        size="small"
                                        appearance="subtle"
                                        icon={aus ? <Eye20Regular /> : <EyeOff20Regular />}
                                        disabled={busy}
                                        onClick={() => aktivSchalten(l)}
                                      >
                                        {aus ? "Aktivieren" : "Deaktivieren"}
                                      </Button>
                                      <Button
                                        size="small"
                                        appearance="subtle"
                                        icon={<Delete20Regular />}
                                        disabled={busy}
                                        onClick={() => setDialog({ art: "loeschen", leaf: l })}
                                      >
                                        Löschen
                                      </Button>
                                    </span>
                                  ) : null}
                                </div>

                                {editId === l.id ? (
                                  <div className={s.formular}>
                                    <Field label="Bezeichnung der Spezialisierung" required>
                                      <Input
                                        value={editName}
                                        onChange={(_e, d) => setEditName(d.value)}
                                      />
                                    </Field>
                                    <Field label="Erläuterung (i-Tooltip)">
                                      <Textarea
                                        value={editText}
                                        onChange={(_e, d) => setEditText(d.value)}
                                        rows={3}
                                      />
                                    </Field>
                                    <div className={s.aktionen}>
                                      <Button
                                        size="small"
                                        appearance="primary"
                                        icon={<Save20Regular />}
                                        disabled={busy || !editName.trim()}
                                        onClick={() => speichereBlatt(l)}
                                      >
                                        Speichern
                                      </Button>
                                      <Button
                                        size="small"
                                        icon={<Dismiss20Regular />}
                                        disabled={busy}
                                        onClick={cancel}
                                      >
                                        Abbrechen
                                      </Button>
                                    </div>
                                  </div>
                                ) : (
                                  <Text
                                    size={200}
                                    className={mergeClasses(!l.erlaeuterung && s.leer)}
                                  >
                                    {l.erlaeuterung || "— keine Erläuterung —"}
                                  </Text>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    ))}
                  </div>
                </AccordionPanel>
              </AccordionItem>
            );
          })}
        </Accordion>
      )}

      <KatalogDialog
        state={dialog}
        busy={busy}
        onClose={() => setDialog(null)}
        onAnlegen={(data) =>
          ausfuehren(async () => {
            await blattAnlegen(data);
            return `„${data.ebene3}" in „${data.ebene1} / ${data.ebene2}" angelegt.`;
          })
        }
        onGruppeUmbenennen={(ebene1, ebene2, neu) =>
          ausfuehren(async () => {
            const r = await gruppeUmbenennen(ebene1, ebene2, neu);
            return `Gruppe umbenannt — ${anzahl(r.betroffen, "Eintrag", "Einträge")} geändert, ${anzahl(r.firmenAktualisiert, "Firma", "Firmen")} nachgezogen.`;
          })
        }
        onBrancheUmbenennen={(ebene1, neu) =>
          ausfuehren(async () => {
            const r = await brancheUmbenennen(ebene1, neu);
            return `Branche umbenannt — ${anzahl(r.betroffen, "Eintrag", "Einträge")} geändert, ${anzahl(r.firmenAktualisiert, "Firma", "Firmen")} nachgezogen.`;
          })
        }
        onLoeschen={(leaf) =>
          ausfuehren(async () => {
            const r = await blattLoeschen(leaf.id);
            return r.geloescht
              ? `„${leaf.ebene3}" gelöscht.`
              : `„${leaf.ebene3}" wird von ${anzahl(r.verwendung.firmen, "Firma", "Firmen")} getragen und wurde deshalb nur deaktiviert.`;
          })
        }
      />
    </Card>
  );
}

/**
 * Alle Dialoge der Katalogpflege in einer Komponente — sie teilen sich Aufbau
 * und Fußzeile und unterscheiden sich nur in den Feldern. Der Zustand liegt
 * bewusst hier: beim Schließen ist er weg, ein neuer Dialog startet leer.
 */
const KatalogDialog: React.FC<{
  state: DialogState | null;
  busy: boolean;
  onClose: () => void;
  /** Alle Aktionen antworten mit `null` bei Erfolg, sonst mit dem Fehlertext. */
  onAnlegen: (data: {
    ebene1: string;
    ebene2: string;
    ebene3: string;
    erlaeuterung: string;
  }) => Promise<string | null>;
  onGruppeUmbenennen: (ebene1: string, ebene2: string, neu: string) => Promise<string | null>;
  onBrancheUmbenennen: (ebene1: string, neu: string) => Promise<string | null>;
  onLoeschen: (leaf: Leaf) => Promise<string | null>;
}> = ({ state, busy, onClose, onAnlegen, onGruppeUmbenennen, onBrancheUmbenennen, onLoeschen }) => {
  const s = useStyles();
  const [e1, setE1] = React.useState("");
  const [e2, setE2] = React.useState("");
  const [e3, setE3] = React.useState("");
  const [text, setText] = React.useState("");
  const [neu, setNeu] = React.useState("");
  const [verwendung, setVerwendung] = React.useState<KatalogVerwendung | null>(null);
  const [pruefe, setPruefe] = React.useState(false);
  const [fehler, setFehler] = React.useState<string | null>(null);

  // Jeder neu geöffnete Dialog startet mit frischen Feldern.
  React.useEffect(() => {
    setE1(state && "ebene1" in state ? state.ebene1 : "");
    setE2(state && "ebene2" in state ? state.ebene2 : "");
    setE3("");
    setText("");
    setNeu(
      state?.art === "rename-branche"
        ? state.ebene1
        : state?.art === "rename-gruppe"
          ? state.ebene2
          : "",
    );
    setVerwendung(null);
    setFehler(null);
  }, [state]);

  // Vor dem Löschen zählen, wie viele Firmen den Eintrag tragen — davon hängt
  // ab, ob gelöscht oder nur deaktiviert wird.
  React.useEffect(() => {
    if (state?.art !== "loeschen") return;
    let lebt = true;
    setPruefe(true);
    verwendungLaden(state.leaf.id)
      .then((v) => lebt && setVerwendung(v))
      .catch((e) => lebt && setFehler((e as Error).message))
      .finally(() => lebt && setPruefe(false));
    return () => {
      lebt = false;
    };
  }, [state]);

  if (!state) return null;

  const titel =
    state.art === "neu-branche"
      ? "Neue Branche anlegen"
      : state.art === "neu-gruppe"
        ? "Neue Branchengruppe anlegen"
        : state.art === "neu-spez"
          ? "Neue Spezialisierung anlegen"
          : state.art === "rename-branche"
            ? "Branche umbenennen"
            : state.art === "rename-gruppe"
              ? "Branchengruppe umbenennen"
              : "Eintrag löschen";

  const anlegen = state.art === "neu-branche" || state.art === "neu-gruppe" || state.art === "neu-spez";
  const anlegenBereit = e1.trim() !== "" && e2.trim() !== "" && e3.trim() !== "";
  const umbenennenBereit =
    neu.trim() !== "" &&
    neu.trim() !== (state.art === "rename-branche" ? state.ebene1 : state.art === "rename-gruppe" ? state.ebene2 : "");

  const bestaetigen = () => {
    setFehler(null);
    const lauf = anlegen
      ? onAnlegen({ ebene1: e1.trim(), ebene2: e2.trim(), ebene3: e3.trim(), erlaeuterung: text.trim() })
      : state.art === "rename-branche"
        ? onBrancheUmbenennen(state.ebene1, neu.trim())
        : state.art === "rename-gruppe"
          ? onGruppeUmbenennen(state.ebene1, state.ebene2, neu.trim())
          : onLoeschen(state.leaf);
    // Bei Erfolg schließt der Aufrufer den Dialog. Scheitert es, bleibt er offen
    // — dann muss der Grund HIER stehen („„SaaS" gibt es in „IT / Software"
    // bereits."), sonst passiert nach dem Klick sichtbar nichts.
    void lauf.then((meldung) => meldung !== null && setFehler(meldung));
  };

  const bereit = anlegen
    ? anlegenBereit
    : state.art === "loeschen"
      ? !pruefe && verwendung !== null
      : umbenennenBereit;

  return (
    <Dialog open modalType="modal" onOpenChange={(_e, d) => !d.open && onClose()}>
      <DialogSurface>
        <DialogBody>
          <DialogTitle>{titel}</DialogTitle>
          <DialogContent>
            <div className={s.formular}>
              {fehler ? (
                <MessageBar intent="error">
                  <MessageBarBody>{fehler}</MessageBarBody>
                </MessageBar>
              ) : null}

              {anlegen ? (
                <>
                  {/* Eine Gruppe existiert nur über ihre Blätter — deshalb
                      verlangt auch „neue Gruppe" die erste Spezialisierung. */}
                  {state.art !== "neu-branche" ? (
                    <MessageBar intent="info">
                      <MessageBarBody>
                        {state.art === "neu-gruppe"
                          ? "Eine Branchengruppe entsteht mit ihrer ersten Spezialisierung — ohne sie gäbe es nichts zu speichern."
                          : "Die neue Spezialisierung wird sofort in Anlage und Akte auswählbar."}
                      </MessageBarBody>
                    </MessageBar>
                  ) : null}

                  {state.art === "neu-branche" ? (
                    <Field label="Branche (Ebene 1)" required>
                      <Input value={e1} onChange={(_e, d) => setE1(d.value)} />
                    </Field>
                  ) : (
                    <Field label="Branche (Ebene 1)">
                      <Text className={s.fest}>{e1}</Text>
                    </Field>
                  )}

                  {state.art === "neu-spez" ? (
                    <Field label="Branchengruppe (Ebene 2)">
                      <Text className={s.fest}>{e2}</Text>
                    </Field>
                  ) : (
                    <Field label="Branchengruppe (Ebene 2)" required>
                      <Input value={e2} onChange={(_e, d) => setE2(d.value)} />
                    </Field>
                  )}

                  <Field label="Spezialisierung (Ebene 3)" required>
                    <Input value={e3} onChange={(_e, d) => setE3(d.value)} />
                  </Field>
                  <Field label="Erläuterung (i-Tooltip)">
                    <Textarea value={text} onChange={(_e, d) => setText(d.value)} rows={3} />
                  </Field>
                </>
              ) : null}

              {state.art === "rename-branche" || state.art === "rename-gruppe" ? (
                <>
                  {/* Der Name gehört allen Blättern gemeinsam — das muss vor
                      dem Speichern dastehen, nicht danach auffallen. */}
                  <MessageBar intent="warning">
                    <MessageBarBody>
                      {state.art === "rename-branche"
                        ? `Die Umbenennung wirkt auf ALLE ${anzahl(state.blaetter, "Spezialisierung", "Spezialisierungen")} in ${anzahl(state.gruppen, "Gruppe", "Gruppen")} dieser Branche. Der Anzeigepfad wird bei den betroffenen Firmen mit nachgezogen; ein handgepflegter Branchen-Freitext aus dem Altbestand bleibt stehen.`
                        : `Die Umbenennung wirkt auf ALLE ${anzahl(state.blaetter, "Spezialisierung", "Spezialisierungen")} dieser Gruppe. Der Anzeigepfad wird bei den betroffenen Firmen mit nachgezogen; ein handgepflegter Branchen-Freitext aus dem Altbestand bleibt stehen.`}
                    </MessageBarBody>
                  </MessageBar>
                  <Field label="Bisher">
                    <Text className={s.fest}>
                      {state.art === "rename-branche"
                        ? state.ebene1
                        : `${state.ebene1} / ${state.ebene2}`}
                    </Text>
                  </Field>
                  <Field label="Neue Bezeichnung" required>
                    <Input value={neu} onChange={(_e, d) => setNeu(d.value)} />
                  </Field>
                </>
              ) : null}

              {state.art === "loeschen" ? (
                <>
                  <Field label="Eintrag">
                    <Text className={s.fest}>
                      {`${state.leaf.ebene1} / ${state.leaf.ebene2} / ${state.leaf.ebene3}`}
                    </Text>
                  </Field>
                  {pruefe ? (
                    <Spinner size="tiny" label="Zuordnungen werden geprüft …" />
                  ) : verwendung ? (
                    <MessageBar intent={verwendung.firmen > 0 ? "warning" : "info"}>
                      <MessageBarBody>
                        {verwendung.firmen > 0
                          ? `${anzahl(verwendung.firmen, "Firma trägt", "Firmen tragen")} diesen Eintrag${verwendung.imPapierkorb > 0 ? ` (davon ${verwendung.imPapierkorb} im Papierkorb)` : ""}. Er wird deshalb NICHT gelöscht, sondern nur deaktiviert: aus der Auswahl in Anlage und Akte verschwunden, bei diesen Firmen weiterhin sichtbar.`
                          : "Keine Firma trägt diesen Eintrag — er wird endgültig gelöscht."}
                      </MessageBarBody>
                    </MessageBar>
                  ) : null}
                </>
              ) : null}
            </div>
          </DialogContent>
          <DialogActions>
            <Button appearance="secondary" disabled={busy} onClick={onClose}>
              Abbrechen
            </Button>
            <Button appearance="primary" disabled={busy || !bereit} onClick={bestaetigen}>
              {anlegen
                ? "Anlegen"
                : state.art === "loeschen"
                  ? verwendung && verwendung.firmen > 0
                    ? "Deaktivieren"
                    : "Löschen"
                  : "Umbenennen"}
            </Button>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
};

export default BranchenKatalogSection;
